import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShieldCheck, FolderHeart, Users, HospitalIcon, Pill, FileText, ArrowRight } from "lucide-react";

const FEATURES = [
  { icon: FolderHeart, title: "One private vault", text: "Keep every report, scan and bill for you and your family in one secure place." },
  { icon: Users, title: "Family profiles", text: "Add your spouse, kids or parents and switch between their records in a tap." },
  { icon: Pill, title: "Track medicines", text: "Note down current medications and dosages so nothing is ever forgotten." },
  { icon: HospitalIcon, title: "Carry it anywhere", text: "Soon: link any hospital you visit — your history follows you, no re-entry." },
];

export default function HealthLanding() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-white text-slate-800" data-testid="health-landing">
      <header className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2 font-display font-bold text-emerald-700 text-lg">
          <ShieldCheck className="h-6 w-6" /> Mediva Health
        </div>
        <div className="flex items-center gap-2">
          <Link to="/health/login"><Button variant="ghost" data-testid="landing-login-btn">Log in</Button></Link>
          <Link to="/health/signup"><Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="landing-signup-btn">Create account</Button></Link>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-12 pb-16 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-100 rounded-full px-3 py-1">
            <FileText className="h-3.5 w-3.5" /> Your records, owned by you
          </span>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight text-slate-900">
            Your family's health, <span className="text-emerald-600">in one place.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl">
            Sign up in seconds to build a private health vault — upload reports, track medicines, and keep notes for
            every member of your family. When you visit a hospital on Mediva, connect and your records travel with you.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/health/signup"><Button size="lg" className="bg-emerald-600 hover:bg-emerald-700" data-testid="hero-signup-btn">Start your vault <ArrowRight className="h-4 w-4 ml-1.5" /></Button></Link>
            <Link to="/health/login"><Button size="lg" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="hero-login-btn">I already have an account</Button></Link>
          </div>
          <p className="mt-4 text-xs text-slate-400 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Private & encrypted in transit. You control who sees your records.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-white border border-emerald-100 rounded-2xl p-5 shadow-sm" data-testid={`feature-${f.title.split(" ")[0].toLowerCase()}`}>
              <div className="h-10 w-10 rounded-xl bg-emerald-100 grid place-items-center text-emerald-700"><f.icon className="h-5 w-5" /></div>
              <div className="mt-3 font-semibold text-slate-900">{f.title}</div>
              <p className="text-sm text-slate-500 mt-1">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-100 py-6 text-center text-xs text-slate-400">
        Mediva Health · A patient-owned records ecosystem · Not a substitute for professional medical advice.
      </footer>
    </div>
  );
}
